// Pure logic of the guests follow-up: figures, breakdowns, filtering, sorting.
// Guests come from /collaborator-portal/guests (no contact details, no tokens).

const normalize = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

export const STATUS_LABELS = { confirmed: "Confirmé", pending: "En attente", declined: "Absent" };

export function normalizeStatus(status) {
  return status === "confirmed" || status === "declined" ? status : "pending";
}

/** People behind one invitation: the guest plus their companions. */
export const peopleOf = (guest) => 1 + Math.max(0, Number(guest.plus_ones_count) || 0);

const emptyBucket = () => ({ total: 0, confirmed: 0, pending: 0, declined: 0, people: 0, confirmedPeople: 0 });

function addTo(bucket, guest) {
  const status = normalizeStatus(guest.rsvp_status);
  bucket.total += 1;
  bucket[status] += 1;
  bucket.people += peopleOf(guest);
  if (status === "confirmed") bucket.confirmedPeople += peopleOf(guest);
}

/** Totals plus a breakdown per group and per side (largest first). */
export function computeGuestStats(guests = []) {
  const overall = emptyBucket();
  const groups = new Map();
  const sides = new Map();
  guests.forEach((guest) => {
    addTo(overall, guest);
    const group = String(guest.group || "").trim() || "Sans groupe";
    const side = String(guest.side || "").trim() || "Non précisé";
    if (!groups.has(group)) groups.set(group, emptyBucket());
    if (!sides.has(side)) sides.set(side, emptyBucket());
    addTo(groups.get(group), guest);
    addTo(sides.get(side), guest);
  });
  const toList = (map) => Array.from(map, ([name, bucket]) => ({ name, ...bucket })).sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, "fr"));
  const answered = overall.confirmed + overall.declined;
  return {
    ...overall,
    answered,
    responseRate: overall.total ? Math.round((answered / overall.total) * 100) : 0,
    groups: toList(groups),
    sides: toList(sides),
  };
}

export const SORTS = {
  name: { label: "Nom (A → Z)", compare: (a, b) => String(a.name).localeCompare(String(b.name), "fr") },
  status: {
    label: "Statut (à relancer d'abord)",
    compare: (a, b) => {
      const order = { pending: 0, confirmed: 1, declined: 2 };
      return order[normalizeStatus(a.rsvp_status)] - order[normalizeStatus(b.rsvp_status)] || String(a.name).localeCompare(String(b.name), "fr");
    },
  },
  group: { label: "Groupe", compare: (a, b) => String(a.group || "~").localeCompare(String(b.group || "~"), "fr") || String(a.name).localeCompare(String(b.name), "fr") },
  people: { label: "Nombre de personnes", compare: (a, b) => peopleOf(b) - peopleOf(a) || String(a.name).localeCompare(String(b.name), "fr") },
};

export function filterGuests(guests, { query = "", status = "all", group = "", side = "", sort = "name" } = {}) {
  const needle = normalize(query);
  const list = guests.filter((guest) => (
    (status === "all" || normalizeStatus(guest.rsvp_status) === status)
    && (!group || (String(guest.group || "").trim() || "Sans groupe") === group)
    && (!side || (String(guest.side || "").trim() || "Non précisé") === side)
    && (!needle || normalize(guest.name).includes(needle) || normalize(guest.group).includes(needle))
  ));
  return list.sort((SORTS[sort] || SORTS.name).compare);
}
